import type { ReactNode } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { LegacyColumnDef } from '@tanstack/react-table/legacy';
import type { ListSort } from '@/hooks/list-params-serde';

/** Column definition for `DataTable`. Import this instead of the raw library type. */
export type DataTableColumn<TData extends RowData, TValue = unknown> = LegacyColumnDef<
  TData,
  TValue
>;

export type DataTableDensity = 'compact' | 'default';

/** Fixed heights: windowing needs to know a row's size without measuring it. */
export const ROW_HEIGHT: Record<DataTableDensity, number> = { compact: 40, default: 52 };
export const HEADER_HEIGHT = 40;
/** Below this many rows the DOM cost is lower than the windowing bookkeeping. */
export const VIRTUALIZE_THRESHOLD = 100;

/**
 * El id de la fila de detalle, para que el botón que la abre pueda apuntarle con
 * `aria-controls` y el lector de pantalla sepa qué se desplegó.
 *
 * Se deriva del id de la fila en un solo lugar: con la tabla escribiendo uno y
 * el botón otro, la relación existe en pantalla y no existe para quien no ve.
 */
export const rowDetailId = (rowId: string): string => `detalle-${rowId}`;

export interface BulkActionContext<TData> {
  ids: string[];
  rows: TData[];
  clear: () => void;
}

export interface DataTableProps<TData extends RowData> {
  /** Stable id — column layout and density are remembered per table, per user. */
  tableId: string;
  data: readonly TData[];
  columns: readonly DataTableColumn<TData>[];
  getRowId: (row: TData) => string;

  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** What failed, named by the view. Defaults to «No pudimos cargar los datos». */
  errorTitle?: string;

  /** Splits "no hay nada" from "nada coincide con el filtro" (ARCHITECTURE §3.6). */
  isFiltered?: boolean;
  onClearFilters?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;

  /** Rows behave like links: click opens, ⌘-click opens a new tab, Enter opens. */
  rowHref?: (row: TData) => string;
  onRowOpen?: (row: TData, options: { newTab: boolean }) => void;

  selectable?: boolean;
  onSelectionChange?: (ids: string[], rows: TData[]) => void;
  bulkActions?: (context: BulkActionContext<TData>) => ReactNode;
  /**
   * Name of a row, for its selection checkbox. Without it the checkbox is
   * announced as "Seleccionar fila 7", which is a position and not a record —
   * useless for deciding whether to tick it.
   */
  rowLabel?: (row: TData) => string;

  /** Controlled sorting; leave out to let the table sort in memory. */
  sort?: ListSort | null;
  onSortChange?: (sort: ListSort | null) => void;
  /** Cómo viene ordenada la lista cuando nadie eligió: la ficha lo dice igual. */
  defaultSortLabel?: string;
  /** Esconde normales/compactas donde la diferencia no se distingue. */
  hideDensity?: boolean;
  /** The caller sorts (server-side). Only meaningful alongside `onSortChange`. */
  manualSorting?: boolean;

  rowActions?: (row: TData) => ReactNode;
  /**
   * Lo que una fila abre DEBAJO de sí, en una fila propia a todo el ancho.
   *
   * Existe para el detalle que no cabe en columnas: una lista de pares
   * etiqueta→valor que cambia de una fila a la siguiente y que, como columnas,
   * obligaría a la tabla a tener una por cada etiqueta que alguna fila trajo.
   *
   * Quien la dibuja decide **cuáles están abiertas** (`expandedIds`) y pone el
   * control que las abre — normalmente en `rowActions`, que ya no propaga el
   * clic a la fila. Así el clic principal de la fila sigue significando una sola
   * cosa, que es la que la tabla siempre prometió: abrir el registro.
   */
  rowDetail?: (row: TData) => ReactNode;
  /**
   * Qué filas tienen el detalle abierto, por el id que devuelve `getRowId`.
   *
   * Controlado por quien dibuja y no por la tabla: el estado sobrevive a que la
   * lista se recargue, y una vista que quiera abrir una fila desde fuera —un
   * enlace, un atajo— no tiene que pelear con un estado interno.
   */
  expandedIds?: ReadonlySet<string>;
  /** Keeps the first column (and the checkbox) in place while scrolling sideways. */
  pinFirstColumn?: boolean;
  /** Extra controls in the table toolbar, left of the column and density menus. */
  toolbar?: ReactNode;
  /** Hides the built-in toolbar entirely when the view provides its own. */
  hideToolbar?: boolean;
  /**
   * Drops the toolbar's row counter. It counts the rows in memory, which is the
   * page; a paginated view already says «1–50 de 10.000» in its footer, and two
   * counters that read differently mean the same thing is a puzzle, not a fact.
   */
  hideRowCount?: boolean;
  density?: DataTableDensity;
  onDensityChange?: (density: DataTableDensity) => void;
  /** Human names for the column menu, when a header is not plain text. */
  columnLabels?: Record<string, string>;
  /**
   * Columns that start hidden. Only a seed: a choice the person already made in
   * the column menu is stored per table and always wins over this.
   */
  defaultHiddenColumns?: readonly string[];
  /**
   * Ids whose figures hang off the right edge, so their header goes with them.
   * A column of amounts is read down its last digit; a heading left over the
   * first one points at nothing.
   */
  numericColumns?: readonly string[];
  /**
   * Ids cuyo contenido se estira con su columna en vez de quedarse en su `size`.
   *
   * EL PROBLEMA QUE RESUELVE. En `table-layout: auto` una celda no tiene ancho
   * propio, así que un texto largo o ensancha la columna o baja a tres líneas —y
   * las filas de alto fijo que el ventaneo necesita se rompen—. La defensa de
   * siempre es un `max-width` en píxeles adentro de la celda, igual al `size` de
   * su columna. Funciona, pero es un ancho FIJO: cuando la tabla tiene pocas
   * columnas y sobra ancho, el navegador reparte el sobrante entre ellas y el
   * contenido se queda recortado en su número, con un hueco a la derecha.
   *
   * Con esto la celda pone `max-width: 0`, que **no** es su ancho dibujado: es
   * lo único que declara para el cálculo del ancho de la columna, o sea nada. La
   * columna la sigue gobernando el encabezado —su `width` es la preferencia y su
   * `min-width` el piso—, el sobrante se reparte igual que siempre, y el bloque
   * de adentro llena lo que le toque y recorta ahí. Un texto largo ya no puede
   * ensanchar nada.
   *
   * Es opt-in: una columna sin `size` que entrara acá se encogería hasta su
   * encabezado, porque no le quedaría quién declare su ancho.
   */
  fillColumns?: readonly string[];
  virtualizeThreshold?: number;
  /** Announced to screen readers as the table's purpose. */
  caption?: string;
  className?: string;
}
